import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Button from '../components/Button';
import Card, { CardBody } from '../components/Card';
import { useAuth } from '../context/AuthContext';
import { UserIcon, MailIcon, LockIcon, AlertCircleIcon, CheckCircleIcon, PhoneIcon } from 'lucide-react';
const RegisterPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    register
  } = useAuth();
  const navigate = useNavigate();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name || !email || !phone || !password || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      const success = await register(name, email, password);
      if (success) {
        navigate('/dashboard');
      } else {
        setError('Registration failed. Email may already be in use.');
      }
    } catch (err) {
      setError('An error occurred during registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-6047">
      <Header data-id="element-6048" />
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-id="element-6049">
        <div className="max-w-md w-full" data-id="element-6050">
          <div className="text-center mb-8" data-id="element-6051">
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-6052">
              Create an account
            </h1>
            <p className="mt-2 text-gray-600" data-id="element-6053">
              Register to access Edo State government services
            </p>
          </div>
          <Card data-id="element-6054">
            <CardBody data-id="element-6055">
              {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center" data-id="element-6056">
                  <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-6057" />
                  <span data-id="element-6058">{error}</span>
                </div>}
              <form onSubmit={handleSubmit} data-id="element-6059">
                <div className="mb-4" data-id="element-6060">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6061">
                    Full Name
                  </label>
                  <div className="relative" data-id="element-6062">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6063">
                      <UserIcon className="h-5 w-5 text-gray-400" data-id="element-6064" />
                    </div>
                    <input id="name" name="name" type="text" autoComplete="name" required value={name} onChange={e => setName(e.target.value)} className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500" placeholder="Enter your full name" data-id="element-6065" />
                  </div>
                </div>
                <div className="mb-4" data-id="element-6066">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6067">
                    Email Address
                  </label>
                  <div className="relative" data-id="element-6068">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6069">
                      <MailIcon className="h-5 w-5 text-gray-400" data-id="element-6070" />
                    </div>
                    <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500" placeholder="Enter your email" data-id="element-6071" />
                  </div>
                </div>
                <div className="mb-4" data-id="element-6072">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6073">
                    Phone Number
                  </label>
                  <div className="relative" data-id="element-6074">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6075">
                      <PhoneIcon className="h-5 w-5 text-gray-400" data-id="element-6076" />
                    </div>
                    <input id="phone" name="phone" type="tel" autoComplete="tel" required value={phone} onChange={e => setPhone(e.target.value)} className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500" placeholder="Enter your phone number" data-id="element-6077" />
                  </div>
                </div>
                <div className="mb-4" data-id="element-6078">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6079">
                    Password
                  </label>
                  <div className="relative" data-id="element-6080">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6081">
                      <LockIcon className="h-5 w-5 text-gray-400" data-id="element-6082" />
                    </div>
                    <input id="password" name="password" type="password" autoComplete="new-password" required value={password} onChange={e => setPassword(e.target.value)} className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500" placeholder="Create a password" data-id="element-6083" />
                  </div>
                </div>
                <div className="mb-6" data-id="element-6084">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6085">
                    Confirm Password
                  </label>
                  <div className="relative" data-id="element-6086">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6087">
                      <LockIcon className="h-5 w-5 text-gray-400" data-id="element-6088" />
                    </div>
                    <input id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500" placeholder="Confirm your password" data-id="element-6089" />
                  </div>
                </div>
                <div className="mb-6" data-id="element-6090">
                  <div className="flex items-start" data-id="element-6091">
                    <div className="flex items-center h-5" data-id="element-6092">
                      <input id="terms" name="terms" type="checkbox" required className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" data-id="element-6093" />
                    </div>
                    <div className="ml-3 text-sm" data-id="element-6094">
                      <label htmlFor="terms" className="text-gray-600" data-id="element-6095">
                        I agree to the{' '}
                        <a href="#" className="text-green-600 hover:text-green-500" data-id="element-6096">
                          Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href="#" className="text-green-600 hover:text-green-500" data-id="element-6097">
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  </div>
                </div>
                <Button type="submit" variant="primary" fullWidth disabled={isLoading} data-id="element-6098">
                  {isLoading ? 'Creating account...' : 'Create account'}
                </Button>
              </form>
              <div className="mt-6 text-center" data-id="element-6099">
                <p className="text-sm text-gray-600" data-id="element-6100">
                  Already have an account?{' '}
                  <Link to="/login" className="font-medium text-green-600 hover:text-green-500" data-id="element-6101">
                    Sign in
                  </Link>
                </p>
              </div>
            </CardBody>
          </Card>
        </div>
      </main>
      <Footer data-id="element-6102" />
    </div>;
};
export default RegisterPage;